/**
 * Batches rapid-fire pickup-arrival events into a single flush instead of one
 * store mutation per event, so a burst of N pickups triggers one grid rebuild,
 * not N.
 *
 * Mechanism: trailing throttle with immediate re-arm, not a debounce. A debounce
 * (reset the timer on every new arrival) would never fire during a sustained
 * burst — the dispatcher would see nothing update for as long as pickups kept
 * streaming in. Instead: the first enqueue after idle opens a fixed window;
 * everything arriving during that window is buffered; when the window closes,
 * whatever accumulated is flushed in one call, and if more arrives afterward, the
 * next window opens immediately. Worst-case per-pickup latency is bounded to one
 * window even under a continuous stream.
 *
 * Buffering is keyed by pickupId via a Map, which gives "last write wins per
 * pickupId within the window, arrival order preserved otherwise" for free —
 * re-setting an existing Map key updates its value without moving its position.
 */
export const PICKUP_ARRIVAL_BATCH_WINDOW_MS = 100;

export const createPickupArrivalBatcher = ({
  windowMs = PICKUP_ARRIVAL_BATCH_WINDOW_MS,
  onFlush
}) => {
  let pending = new Map();
  let timer = null;

  const flush = () => {
    timer = null;
    if (pending.size === 0) {
      return;
    }
    const batch = Array.from(pending.values());
    pending = new Map();
    onFlush(batch);
  };

  const enqueue = (pickupId, data) => {
    const key = pickupId?.toUpperCase();
    if (!key) {
      return;
    }
    pending.set(key, { pickupId, data });
    if (!timer) {
      timer = setTimeout(flush, windowMs);
    }
  };

  const clear = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    pending = new Map();
  };

  const clearPickupIds = (pickupIds = []) => {
    pickupIds.forEach((pickupId) => {
      const key = pickupId?.toUpperCase();
      if (key) {
        pending.delete(key);
      }
    });
    if (pending.size === 0) {
      clear();
    }
  };

  // clear / clearPickupIds drop buffered events without applying them, so a
  // newer HTTP refresh or terminal reset cannot be overwritten when the window
  // closes. dispose is the same as clear (unmount must not flush).
  return { enqueue, clear, clearPickupIds, dispose: clear };
};
